import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Plus, Tags } from 'lucide-react';
import type { Trade } from '@worki/shared';
import { api } from '../../lib/api.js';
import { Button, EmptyState, Input, Spinner } from '../../components/ui.js';
import { Modal } from '../../components/Modal.js';
import { Pill, Row, useAdminAction } from './parts.js';

/**
 * ADM-009 — the trades the sign-up form offers.
 *
 * A table rather than an enum in the code, so a trade added here appears in the
 * dropdown without a deploy. Nothing is ever deleted: a business points at the
 * row it chose, and removing it would take the answer that business gave with
 * it — so a retired trade simply stops being offered.
 */
export function TradesSection() {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Trade | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['platform', 'trades'],
    queryFn: () => api.get<{ items: Trade[] }>('/platform/trades'),
  });

  /* `['trades']` is the sign-up form's own copy of this list — the key it
     caches `/auth/trades` under. */
  const action = useAdminAction([['platform', 'trades'], ['trades']]);

  const rows = list.data?.items ?? [];

  const save = (input: { name: string; position: number; active: boolean }) => {
    const target = editing;
    if (!target) return;
    const request =
      target === 'new'
        ? () => api.post('/platform/trades', input)
        : () => api.patch(`/platform/trades/${target.id}`, input);
    void action.run(request, 'admin.trades.saved').then((ok) => {
      if (ok) setEditing(null);
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="h-9 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" /> {t('admin.trades.add')}
        </button>
        <p className="text-xs text-muted-foreground flex-1">{t('admin.trades.hint')}</p>
      </div>

      {list.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<Tags className="h-7 w-7" />} title={t('admin.trades.empty')} />
      ) : (
        <div className="space-y-2">
          {rows.map((trade) => (
            <Row
              key={trade.id}
              icon={<Tags className="h-4 w-4" aria-hidden />}
              title={trade.name}
              meta={<Pill tone={trade.active ? 'good' : 'neutral'}>{trade.active ? t('admin.trades.active') : t('admin.trades.inactive')}</Pill>}
              onClick={() => setEditing(trade)}
            />
          ))}
        </div>
      )}

      <TradeDialog trade={editing} busy={action.busy} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function TradeDialog({
  trade,
  busy,
  onClose,
  onSave,
}: {
  trade: Trade | 'new' | null;
  busy: boolean;
  onClose: () => void;
  onSave: (input: { name: string; position: number; active: boolean }) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [position, setPosition] = useState('0');
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!trade) return;
    setName(trade === 'new' ? '' : trade.name);
    setPosition(trade === 'new' ? '0' : String(trade.position));
    setActive(trade === 'new' ? true : trade.active);
  }, [trade]);

  return (
    <Modal
      open={trade !== null}
      title={trade === 'new' ? t('admin.trades.add') : t('common.edit')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => onSave({ name: name.trim(), position: Number(position) || 0, active })}
            loading={busy}
            disabled={name.trim() === ''}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{t('admin.trades.name')}</span>
          <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={60} />
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{t('admin.trades.position')}</span>
          <Input
            value={position}
            onChange={(event) => setPosition(event.target.value.replace(/\D/g, '').slice(0, 3))}
            inputMode="numeric"
            className="ltr-nums"
          />
          {/* The number decides the order of a dropdown this screen never shows,
              so on its own it reads as an id. The hint is what makes it a
              position: which way is up, and why the shipped list counts in tens. */}
          <span className="block text-[11px] text-muted-foreground">{t('admin.trades.positionHint')}</span>
        </label>

        <label className="flex items-center gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
            className="h-4 w-4 shrink-0 rounded accent-primary"
          />
          {t('admin.trades.active')}
        </label>
      </div>
    </Modal>
  );
}
